import manifest from "./manifest.json";

export type DepositProvingDomain = { recoveryDigest?: string } & (
  | { chainId: number; solana?: never }
  | {
      chainId?: never;
      solana: {
        cluster: string;
        programId: string;
        allowDevelopmentSetup: boolean;
      };
    }
);

/**
 * Preflight the actual Solana deployment before reading the pool state.
 * Recovery-enabled pools use a separately pinned verifier/key. The recovery
 * digest itself is derived only after the note is constructed, so preflight
 * must select the key family from the deployment's explicit recovery mode.
 * The worker still checks the actual digest via assertDepositProvingDomain.
 */
export function assertSolanaDepositProvingSetup(setup: {
  cluster: string;
  programId: string;
  allowDevelopmentSetup: boolean;
  onchainRecoveryEnabled: boolean;
}): void {
  const recovery = setup?.onchainRecoveryEnabled === true;
  const pin = recovery
    ? manifest.recovery.deployments.solana
    : manifest.solanaDevnet;
  const vkSha256 = recovery ? manifest.recovery.vk.sha256 : manifest.vk.sha256;
  if (
    !setup ||
    setup.cluster !== "devnet" ||
    setup.cluster !== pin.cluster ||
    setup.programId !== pin.programId ||
    setup.allowDevelopmentSetup !== true ||
    pin.depositVkSha256 !== vkSha256
  ) {
    throw new Error(
      "Development deposit proving is restricted to the pinned Solana devnet deployment",
    );
  }
}

/** Shared by the caller and worker: development keys stay pinned to testnets. */
export function assertDepositProvingDomain(domain: DepositProvingDomain): void {
  const recovery = domain.recoveryDigest !== undefined;
  if (domain.solana !== undefined) {
    if (domain.chainId !== undefined || !domain.solana) {
      throw new Error(
        "Development deposit proving is restricted to the pinned Solana devnet deployment",
      );
    }
    assertSolanaDepositProvingSetup({
      cluster: domain.solana.cluster,
      programId: domain.solana.programId,
      allowDevelopmentSetup: domain.solana.allowDevelopmentSetup,
      onchainRecoveryEnabled: recovery,
    });
    return;
  }
  if (recovery) {
    const pins = [
      manifest.recovery.deployments.base,
      manifest.recovery.deployments.doge,
    ];
    if (
      !pins.some(
        (pin) =>
          pin.chainId === domain.chainId &&
          pin.depositVkSha256 === manifest.recovery.vk.sha256 &&
          pin.verifierCodeHash ===
            manifest.recovery.deployments.base.verifierCodeHash,
      )
    )
      throw new Error(
        "Development deposit proving is restricted to Base Sepolia and DogeOS Chikyu",
      );
    return;
  }
  const dogeos = manifest.dogeosChikyu;
  if (
    domain.chainId !== manifest.chainId &&
    !(
      domain.chainId === dogeos.chainId &&
      dogeos.depositVkSha256 === manifest.vk.sha256 &&
      dogeos.verifierCodeHash === manifest.verifierCodeHash
    )
  ) {
    throw new Error(
      "Development deposit proving is restricted to Base Sepolia and DogeOS Chikyu",
    );
  }
}
